import type { useHistory } from "react-router-dom";
import { z } from "zod";
import { sessionStore } from "../../../lib/store";

/**
 * Whether Back has a page of OURS to return to.
 *
 * `history.length` cannot answer this. It counts the TAB: a reader who visited
 * two other sites before following a shared link has a length of three and
 * nothing of ours behind them, so Back
 * walks them out of the app; and the same number in a fresh tab means the
 * opposite. Neither reading is about this app at all.
 *
 * React Router stamps every entry it pushes with a `key`. A cold entry — a
 * typed URL, a shared link, a notification tap — has no router state, so the
 * absence of that key is exactly the question being asked. It survives a
 * reload, which is right: the page behind it survives too.
 *
 * `history.state` is data from outside the type system, so it is parsed rather
 * than probed: `state?.key` would need the optional chaining the house rules
 * forbid, and `"key" in state` the `in` operator they forbid for the same
 * reason.
 *
 * The key is necessary, not sufficient: a `history.replace` stamps one too. The
 * notification router REPLACES its own entry with the page a push names, so a
 * push that boots the native app lands on a stamped entry with nothing behind
 * it. `replaceInPlace` records the key of such a replacement in the session,
 * so the entry stays cold after a round trip to another page and back. The
 * record stays out of the entry's location state, which each page parses with
 * its own schema. A tab of length 1 also settles it, because there is no entry
 * behind at all.
 */
const routerHistoryEntrySchema = z.object({ key: z.string().min(1) });

// The keys of the entries that replaced a cold entry, newest last. A router
// key is unique to its entry, so the list only needs the recent ones.
const COLD_REPLACEMENTS_KEPT = 20;
const coldReplacementKeysStore = sessionStore(
  "cold-replacement-keys",
  [],
  z.array(z.string())
);

export function canGoBackInApp(): boolean {
  if (globalThis.history.length <= 1) return false;
  const entry = routerHistoryEntrySchema.safeParse(globalThis.history.state);
  if (!entry.success) return false;
  return !coldReplacementKeysStore.get().includes(entry.data.key);
}

/**
 * Replace this entry with `path`, and keep it cold when it was: a redirect
 * from a cold link still has no page of ours behind it.
 */
export function replaceInPlace(
  history: Pick<ReturnType<typeof useHistory>, "replace">,
  path: string
): void {
  const wasCold = !canGoBackInApp();
  history.replace(path);
  if (!wasCold) return;
  const entry = routerHistoryEntrySchema.safeParse(globalThis.history.state);
  if (!entry.success) {
    throw new Error("should not happen: the router replaced without a key");
  }
  coldReplacementKeysStore.set((keys) =>
    [...keys, entry.data.key].slice(-COLD_REPLACEMENTS_KEPT)
  );
}

/**
 * Back for a page with no fixed parent: the page before, when it is one of
 * ours, or `fallback` in place of this entry.
 */
export function goBackInApp(
  history: Pick<ReturnType<typeof useHistory>, "goBack" | "replace">,
  fallback: string
): void {
  if (canGoBackInApp()) {
    history.goBack();
    return;
  }
  history.replace(fallback);
}
